import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MutableRefObject } from "react";

import {
  applyInversePatch,
  reduceGameAction,
} from "../../shared/domain/gameReducer";
import type {
  GameDomainDocument,
  GameInversePatch,
} from "../../shared/domain/gameReducer";
import { gameToView, PLAY_LOG_TYPES } from "../data/viewModel";
import type {
  ActiveGameViewModel,
  EventModel,
  GameAction,
  GameModel,
  RemoteTimestamp,
} from "../shared/types/contracts";

const toMillis = (timestamp: RemoteTimestamp | undefined): number => {
  if (typeof timestamp === "number") return timestamp;
  if (timestamp instanceof Date) return timestamp.getTime();
  if (
    typeof timestamp === "object" &&
    timestamp !== null &&
    "toMillis" in timestamp &&
    typeof timestamp.toMillis === "function"
  ) {
    return timestamp.toMillis();
  }
  return 0;
};

function recordValue(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function undoTarget(
  rawGame: GameModel | null | undefined,
  projections: GameProjection[],
  eventDocs: EventModel[] | null | undefined,
): { inversePatch: unknown; summary: string | null } | null {
  const undoStack = rawGame?.undoStack ?? [];
  const targetEventId = undoStack[undoStack.length - 1];
  if (!targetEventId) return null;
  const projection = projections.find(
    (candidate) => candidate.actionId === targetEventId,
  );
  if (projection?.inversePatch) return projection;
  const targetEvent = (eventDocs ?? []).find(
    (event) => event.id === targetEventId,
  );
  return targetEvent?.inversePatch
    ? {
        inversePatch: targetEvent.inversePatch,
        summary: targetEvent.summary,
      }
    : null;
}

function undoCrossesHalfInning(
  rawGame: GameModel | null | undefined,
  projections: GameProjection[],
  eventDocs: EventModel[] | null | undefined,
): boolean {
  const inversePatch = recordValue(
    undoTarget(rawGame, projections, eventDocs)?.inversePatch,
  );
  if (inversePatch?.crossedHalfInning === true) return true;
  const priorState = recordValue(inversePatch?.state);
  const currentState = rawGame?.state;
  return (
    !!priorState &&
    !!currentState &&
    (priorState.inning !== currentState.inning ||
      priorState.batting !== currentState.batting)
  );
}

function sanitizeForPrediction(rawGame: GameModel): GameModel {
  return Object.fromEntries(
    Object.entries(rawGame).map(([key, value]) => [
      key,
      recordValue(value) && typeof recordValue(value)?.toMillis === "function"
        ? toMillis(value as RemoteTimestamp)
        : value,
    ]),
  ) as unknown as GameModel;
}

export type GameProjectionInput = {
  [Type in GameAction["type"]]: {
    actionId: string;
    type: Type;
    payload: Extract<GameAction, { type: Type }>["payload"];
  };
}[GameAction["type"]];

export interface GameProjectionHandle {
  expectedRevision: number;
  acknowledgement: Promise<void> | null;
}

interface GameProjection {
  actionId: string;
  expectedRevision: number;
  raw: GameModel;
  summary: string | null;
  jersey: string | null;
  projectsJersey: boolean;
  inversePatch: GameInversePatch | null;
  committedRevision: number | null;
  committedAt: number | null;
  responseTeamSequence: number | null;
  gameAcknowledged: boolean;
  eventAcknowledged: boolean;
  teamAcknowledged: boolean;
  resolveAcknowledgement: () => void;
  acknowledgement: Promise<void>;
}

interface OptimisticGameOptions {
  scopeKey: string;
  gameDoc: GameModel | null | undefined;
  eventDocs: EventModel[] | null | undefined;
  serverJerseyHolderPlayerId: string | null;
  baseJerseyHolderPlayerId: string | null;
  teamUpdatedAt: RemoteTimestamp | undefined;
}

export interface OptimisticGameController {
  rawGame: GameModel | null | undefined;
  game: ActiveGameViewModel | null;
  log: string[];
  undoCrossesHalfInning: boolean;
  jerseyHolderPlayerId: string | null;
  project: (input: GameProjectionInput) => GameProjectionHandle;
  projectUndo: (
    actionId: string,
  ) => { expectedRevision: number; acknowledgement: Promise<void> } | null;
  markCommitted: (actionId: string, revision: number) => void;
  reject: (actionId: string) => void;
  clearOptimistic: () => void;
}

function snapshotSequence<Value>(
  reference: MutableRefObject<{ value: Value; sequence: number }>,
  value: Value,
): number {
  if (reference.current.value !== value) {
    reference.current = {
      value,
      sequence: reference.current.sequence + 1,
    };
  }
  return reference.current.sequence;
}

export function useOptimisticGame({
  scopeKey,
  gameDoc,
  eventDocs,
  serverJerseyHolderPlayerId,
  baseJerseyHolderPlayerId,
  teamUpdatedAt,
}: OptimisticGameOptions): OptimisticGameController {
  const [, render] = useState(0);
  const projectionsRef = useRef<GameProjection[]>([]);
  const scopeRef = useRef(scopeKey);
  const gameRef = useRef(gameDoc);
  const eventsRef = useRef(eventDocs);
  const jerseyRef = useRef(serverJerseyHolderPlayerId);
  const baseJerseyRef = useRef(baseJerseyHolderPlayerId);
  const teamUpdatedAtRef = useRef(teamUpdatedAt);
  const teamSnapshotRef = useRef({
    value: `${serverJerseyHolderPlayerId}:${toMillis(teamUpdatedAt)}`,
    sequence: 0,
  });
  gameRef.current = gameDoc;
  eventsRef.current = eventDocs;
  jerseyRef.current = serverJerseyHolderPlayerId;
  baseJerseyRef.current = baseJerseyHolderPlayerId;
  teamUpdatedAtRef.current = teamUpdatedAt;
  const teamSequence = snapshotSequence(
    teamSnapshotRef,
    `${serverJerseyHolderPlayerId}:${toMillis(teamUpdatedAt)}`,
  );

  const clearOptimistic = useCallback(() => {
    const pending = projectionsRef.current;
    projectionsRef.current = [];
    pending.forEach((projection) => projection.resolveAcknowledgement());
    render((value) => value + 1);
  }, []);

  useEffect(() => {
    if (scopeRef.current === scopeKey) return;
    scopeRef.current = scopeKey;
    clearOptimistic();
  }, [clearOptimistic, scopeKey]);

  const reconcile = useCallback(() => {
    const events = eventsRef.current ?? [];
    const currentGame = gameRef.current;
    const currentJersey = jerseyRef.current;
    const currentTeamSequence = teamSnapshotRef.current.sequence;
    let changed = false;
    const remaining: GameProjection[] = [];
    for (const projection of projectionsRef.current) {
      const event = events.find(
        (candidate) => candidate.id === projection.actionId,
      );
      if (event) {
        if (!projection.eventAcknowledged) changed = true;
        projection.eventAcknowledged = true;
        projection.committedAt = toMillis(event.occurredAt);
        if (
          projection.committedRevision === null &&
          Number.isInteger(event.committedRevision)
        ) {
          projection.committedRevision = event.committedRevision;
          projection.responseTeamSequence = currentTeamSequence;
          changed = true;
        }
      }
      if (
        projection.committedRevision !== null &&
        currentGame &&
        currentGame.revision >= projection.committedRevision
      ) {
        if (!projection.gameAcknowledged) changed = true;
        projection.gameAcknowledged = true;
      }
      if (!projection.projectsJersey) {
        projection.teamAcknowledged = true;
      } else if (projection.committedRevision !== null) {
        const exact = currentJersey === projection.jersey;
        const superseded =
          projection.gameAcknowledged &&
          projection.eventAcknowledged &&
          projection.committedAt !== null &&
          projection.committedAt > 0 &&
          toMillis(teamUpdatedAtRef.current) > projection.committedAt;
        const legacySuperseded =
          projection.gameAcknowledged &&
          projection.eventAcknowledged &&
          (projection.committedAt ?? 0) === 0 &&
          projection.responseTeamSequence !== null &&
          currentTeamSequence > projection.responseTeamSequence;
        if (exact || superseded || legacySuperseded) {
          if (!projection.teamAcknowledged) changed = true;
          projection.teamAcknowledged = true;
        }
      }
      if (
        projection.gameAcknowledged &&
        projection.eventAcknowledged &&
        projection.teamAcknowledged
      ) {
        projection.resolveAcknowledgement();
        changed = true;
      } else {
        remaining.push(projection);
      }
    }
    if (remaining.length !== projectionsRef.current.length) {
      projectionsRef.current = remaining;
    }
    if (changed) render((value) => value + 1);
  }, []);

  useEffect(reconcile, [
    gameDoc,
    eventDocs,
    serverJerseyHolderPlayerId,
    teamSequence,
    reconcile,
  ]);

  const pendingGame = [...projectionsRef.current]
    .reverse()
    .find((projection) => !projection.gameAcknowledged);
  const rawGame = pendingGame?.raw ?? gameDoc;
  const game = useMemo(() => gameToView(rawGame), [rawGame]);
  const pendingJersey = [...projectionsRef.current]
    .reverse()
    .find(
      (projection) => projection.projectsJersey && !projection.teamAcknowledged,
    );
  const jerseyHolderPlayerId = pendingJersey
    ? pendingJersey.jersey
    : baseJerseyHolderPlayerId;
  const eventIds = new Set((eventDocs ?? []).map((event) => event.id));
  const optimisticLog = [...projectionsRef.current]
    .reverse()
    .filter(
      (projection) => projection.summary && !eventIds.has(projection.actionId),
    )
    .map((projection) => projection.summary as string);
  const authoritativeLog = (eventDocs ?? [])
    .filter((event) => PLAY_LOG_TYPES.has(event.type))
    .sort(
      (left, right) =>
        (right.committedRevision ?? 0) - (left.committedRevision ?? 0),
    )
    .map((event) => event.summary);
  const log = [...optimisticLog, ...authoritativeLog];
  const undoCrossesHalfInningBoundary = undoCrossesHalfInning(
    rawGame,
    projectionsRef.current,
    eventDocs,
  );

  const project = useCallback(
    (input: GameProjectionInput): GameProjectionHandle => {
      const previous = [...projectionsRef.current]
        .reverse()
        .find((projection) => !projection.gameAcknowledged);
      const currentRawGame = previous?.raw ?? gameRef.current;
      const pendingJersey = [...projectionsRef.current]
        .reverse()
        .find(
          (projection) =>
            projection.projectsJersey && !projection.teamAcknowledged,
        );
      const currentJersey = pendingJersey
        ? pendingJersey.jersey
        : baseJerseyRef.current;
      const expectedRevision = currentRawGame?.revision ?? 0;
      if (!currentRawGame) {
        return { expectedRevision, acknowledgement: null };
      }
      try {
        const result = reduceGameAction(
          sanitizeForPrediction(currentRawGame) as GameModel &
            GameDomainDocument,
          { jerseyHolderPlayerId: currentJersey },
          { type: input.type, payload: input.payload },
        );
        const undoStack = result.pushUndo
          ? [...(currentRawGame.undoStack ?? []), input.actionId]
          : (currentRawGame.undoStack ?? []);
        let resolveAcknowledgement!: () => void;
        const acknowledgement = new Promise<void>((resolve) => {
          resolveAcknowledgement = resolve;
        });
        const projectsJersey =
          !!result.teamEffects && "jerseyHolderPlayerId" in result.teamEffects;
        const projection: GameProjection = {
          actionId: input.actionId,
          expectedRevision,
          raw: {
            ...result.nextGame,
            revision: expectedRevision + 1,
            undoStack,
          },
          summary: result.audit?.summary ?? null,
          jersey: projectsJersey
            ? (result.teamEffects.jerseyHolderPlayerId ?? null)
            : currentJersey,
          projectsJersey,
          inversePatch: result.inversePatch,
          committedRevision: null,
          committedAt: null,
          responseTeamSequence: null,
          gameAcknowledged: false,
          eventAcknowledged: false,
          teamAcknowledged: !projectsJersey,
          resolveAcknowledgement,
          acknowledgement,
        };
        projectionsRef.current = [...projectionsRef.current, projection];
        render((value) => value + 1);
        return { expectedRevision, acknowledgement };
      } catch {
        return { expectedRevision, acknowledgement: null };
      }
    },
    [],
  );

  const projectUndo = useCallback(
    (
      actionId: string,
    ): { expectedRevision: number; acknowledgement: Promise<void> } | null => {
      const previous = [...projectionsRef.current]
        .reverse()
        .find((projection) => !projection.gameAcknowledged);
      const currentRawGame = previous?.raw ?? gameRef.current;
      if (!currentRawGame?.undoStack?.length) return null;
      const target = undoTarget(
        currentRawGame,
        projectionsRef.current,
        eventsRef.current,
      );
      if (!target) return null;
      const pendingJersey = [...projectionsRef.current]
        .reverse()
        .find(
          (projection) =>
            projection.projectsJersey && !projection.teamAcknowledged,
        );
      const currentJersey = pendingJersey
        ? pendingJersey.jersey
        : baseJerseyRef.current;
      try {
        const applied = applyInversePatch(
          sanitizeForPrediction(currentRawGame) as GameModel &
            GameDomainDocument,
          target.inversePatch,
        );
        let resolveAcknowledgement!: () => void;
        const acknowledgement = new Promise<void>((resolve) => {
          resolveAcknowledgement = resolve;
        });
        const expectedRevision = currentRawGame.revision;
        const projection: GameProjection = {
          actionId,
          expectedRevision,
          raw: {
            ...applied.nextGame,
            revision: expectedRevision + 1,
            undoStack: currentRawGame.undoStack.slice(0, -1),
          },
          summary: target.summary ? `Undid: ${target.summary}` : null,
          jersey: applied.teamEffects.jerseyHolderPlayerId,
          projectsJersey: true,
          inversePatch: null,
          committedRevision: null,
          committedAt: null,
          responseTeamSequence: null,
          gameAcknowledged: false,
          eventAcknowledged: false,
          teamAcknowledged:
            currentJersey === applied.teamEffects.jerseyHolderPlayerId,
          resolveAcknowledgement,
          acknowledgement,
        };
        projectionsRef.current = [...projectionsRef.current, projection];
        render((value) => value + 1);
        return { expectedRevision, acknowledgement };
      } catch {
        return null;
      }
    },
    [],
  );

  const markCommitted = useCallback(
    (actionId: string, revision: number) => {
      const projection = projectionsRef.current.find(
        (candidate) => candidate.actionId === actionId,
      );
      if (!projection) return;
      projection.committedRevision = revision;
      projection.responseTeamSequence = teamSnapshotRef.current.sequence;
      reconcile();
    },
    [reconcile],
  );

  const reject = useCallback((actionId: string) => {
    const index = projectionsRef.current.findIndex(
      (projection) => projection.actionId === actionId,
    );
    if (index < 0) return;
    const rejected = projectionsRef.current.slice(index);
    projectionsRef.current = projectionsRef.current.slice(0, index);
    rejected.forEach((projection) => projection.resolveAcknowledgement());
    render((value) => value + 1);
  }, []);

  return {
    rawGame,
    game,
    log,
    undoCrossesHalfInning: undoCrossesHalfInningBoundary,
    jerseyHolderPlayerId,
    project,
    projectUndo,
    markCommitted,
    reject,
    clearOptimistic,
  };
}
